import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Target, RefreshCw, AlertCircle, ExternalLink } from "lucide-react";
import { API } from "../constants";

const REFRESH_MS = 5 * 60 * 1000; // 5 minutos (el backend cachea 5 min)

function formatVol(v) {
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

function timeAgo(isoDate) {
  if (!isoDate) return "";
  const s = Math.floor((Date.now() - new Date(isoDate).getTime()) / 1000);
  if (s < 60) return `hace ${s}s`;
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  return `hace ${Math.floor(s / 3600)} h`;
}

export function PredictionsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPredictions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/predictions/top`, { timeout: 15000 });
      setData(res.data);
      setError(null);
    } catch (e) {
      console.error("Error cargando predicciones:", e);
      setError("Predicciones no disponibles temporalmente. Reintentando…");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPredictions();
    const t = setInterval(fetchPredictions, REFRESH_MS);
    return () => clearInterval(t);
  }, [fetchPredictions]);

  const rows = data?.predictions || [];

  return (
    <div className="bg-gray-900/30 border border-gray-800 rounded-xl p-4" data-testid="predictions-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-cyan-400" /> Market Prediction
          <span className="text-[10px] text-gray-500 font-normal border border-gray-700 rounded px-1.5 py-0.5">Polymarket</span>
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {data?.updated_at && <span>Act. {timeAgo(data.updated_at)}</span>}
          <button onClick={fetchPredictions} className="p-1.5 hover:bg-gray-800 rounded-lg transition-colors" title="Actualizar">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {loading && (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => <div key={i} className="animate-pulse h-16 bg-gray-800/60 rounded-lg" />)}
        </div>
      )}

      {!loading && error && (
        <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-2 overflow-y-auto pr-1" style={{ maxHeight: "560px" }}>
          {rows.map((p, i) => (
            <a
              key={i}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block p-3 bg-gray-900/50 border border-gray-800 rounded-lg hover:border-cyan-500/40 transition-colors group"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="text-sm text-gray-200 leading-snug line-clamp-2 flex-1">{p.question}</p>
                <ExternalLink className="w-3.5 h-3.5 text-gray-600 group-hover:text-cyan-400 flex-shrink-0 mt-0.5 transition-colors" />
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden flex">
                  <div className="bg-emerald-500/80 h-full" style={{ width: `${p.yes_pct}%` }} />
                  <div className="bg-red-500/60 h-full" style={{ width: `${100 - p.yes_pct}%` }} />
                </div>
                <span className="text-xs font-mono font-semibold text-emerald-400 w-12 text-right">{p.yes_pct}% SÍ</span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500">
                <span>Vol 24h {formatVol(p.volume_24h)}</span>
                {p.end_date && <span>Cierra {new Date(p.end_date).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}</span>}
              </div>
            </a>
          ))}
        </div>
      )}

      <p className="text-[10px] text-gray-600 mt-3">
        Probabilidad implícita según el precio del mercado en Polymarket. No es asesoramiento financiero.
      </p>
    </div>
  );
}

export default PredictionsPanel;
